import Grid from '@mui/material/Grid';
import CheckCircle from '@mui/icons-material/CheckCircle';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import {
  BarFill,
  BarRow,
  BarTrack,
  ComparisonTableWrapper,
  DataRow,
  EmptyState,
  ExecutiveScore,
  MatchTrack,
  PanelTitle,
  PdiCard,
  PdiGrid,
  RadarLegend,
  ReportHeaderRow,
  ReportPage,
  ReportPanel,
  SummaryText,
  reportColors,
} from './style';

const radarData = [
  { axis: 'ADA', candidato: 72, dna: 76 },
  { axis: 'AUT', candidato: 75, dna: 72 },
  { axis: 'COM', candidato: 76, dna: 70 },
  { axis: 'CONF', candidato: 78, dna: 88 },
  { axis: 'CTRL', candidato: 74, dna: 85 },
  { axis: 'EXE', candidato: 82, dna: 88 },
  { axis: 'RESU', candidato: 78, dna: 78 },
  { axis: 'LID', candidato: 68, dna: 45 },
  { axis: 'ORG', candidato: 80, dna: 86 },
  { axis: 'REL', candidato: 70, dna: 70 },
  { axis: 'RES', candidato: 74, dna: 82 },
  { axis: 'DEC', candidato: 77, dna: 84 },
];

const attention = [
  {
    code: 'AC',
    label: 'Atenção Concentrada',
    value: 40,
    tag: 'Médio',
    tone: 'warning' as const,
  },
  {
    code: 'AD',
    label: 'Atenção Dividida',
    value: 99,
    tag: 'Superior',
    tone: 'success' as const,
  },
  {
    code: 'AA',
    label: 'Atenção Alternada',
    value: 90,
    tag: 'Superior',
    tone: 'success' as const,
  },
  {
    code: 'AG',
    label: 'Atenção Geral',
    value: 80,
    tag: 'Médio Superior',
    tone: 'success' as const,
  },
];

const comparison = [
  { c: 'Adaptabilidade', cand: 72, dna: 76, gap: -4, crit: 'Alta' },
  { c: 'Autonomia', cand: 75, dna: 72, gap: 3, crit: 'Alta' },
  { c: 'Comunicação', cand: 76, dna: 70, gap: 6, crit: 'Média' },
  { c: 'Conformidade', cand: 78, dna: 88, gap: -10, crit: 'Muito alta' },
  { c: 'Controle Emocional', cand: 74, dna: 85, gap: -11, crit: 'Muito alta' },
  { c: 'Execução', cand: 82, dna: 88, gap: -6, crit: 'Muito alta' },
  { c: 'Foco em Resultados', cand: 78, dna: 78, gap: 0, crit: 'Alta' },
  { c: 'Liderança', cand: 68, dna: 45, gap: 23, crit: 'Baixa' },
  { c: 'Organização', cand: 80, dna: 86, gap: -6, crit: 'Muito alta' },
  {
    c: 'Relacionamento Interpessoal',
    cand: 70,
    dna: 70,
    gap: 0,
    crit: 'Média',
  },
  { c: 'Resiliência', cand: 74, dna: 82, gap: -8, crit: 'Alta' },
  { c: 'Tomada de Decisão', cand: 77, dna: 84, gap: -7, crit: 'Muito alta' },
];

const mcgLayers = [
  {
    label: 'MS Executive Control',
    value: 80,
    tag: 'Muito Alto',
    tone: 'success' as const,
  },
  {
    label: 'MS Leadership',
    value: 57,
    tag: 'Executor técnico',
    tone: 'warning' as const,
  },
  { label: 'MS Resilience', value: 60, tag: 'Alto', tone: 'success' as const },
  {
    label: 'Impulsive Risk',
    value: 29,
    tag: 'Baixo',
    tone: 'success' as const,
  },
];

const pdi = [
  {
    n: 1,
    focus: 'Conformidade e segurança',
    obj: 'Fortalecer aplicação rigorosa de procedimentos, checklists e padrões de execução.',
    trilhas: [
      'NR-10 e procedimento seguro aplicado',
      'Rotina de checklist antes da execução',
      'Comunicação de risco e quase acidente',
    ],
    ganho:
      'Redução de desvios operacionais e maior previsibilidade em atividades críticas.',
  },
  {
    n: 2,
    focus: 'Controle emocional sob pressão',
    obj: 'Ampliar estabilidade comportamental em cenários de urgência, cobrança e variáveis.',
    trilhas: [
      'Técnicas de autorregulação em campo',
      'Tomada de decisão em situação crítica',
      'Gestão de pressão e foco operacional',
    ],
    ganho: 'Maior consistência de resposta em contextos exigentes e sensíveis.',
  },
  {
    n: 3,
    focus: 'Organização e execução',
    obj: 'Consolidar método de trabalho, registro de etapas e disciplina de fechamento das entregas.',
    trilhas: [
      'Planejamento diário de rota e atividade',
      'Padronização de registros operacionais',
      'Qualidade e rastreabilidade da entrega',
    ],
    ganho:
      'Entregas mais consistentes, auditáveis e alinhadas ao padrão da função.',
  },
];

const ReportHeader = () => (
  <ReportHeaderRow>
    <span className="report-label">MS Avaliação | JOB FIT MS</span>
    <span className="report-title">Job Fit Report | MCG v4.1</span>
  </ReportHeaderRow>
);

export const JobFitReportMock = () => {
  return (
    <div>
      {/* Página 1 — Executive */}
      <ReportPage>
        <ReportHeader />

        <ReportPanel sx={{ marginBottom: '20px' }}>
          <PanelTitle>Dados da Avaliação</PanelTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DataRow>
                <span className="label">Cargo analisado</span>
                <span className="value strong">
                  AUX. ELETRICISTA DE DISTRIBUIÇÃO
                </span>
              </DataRow>
              <DataRow>
                <span className="label">Empresa</span>
                <span className="value">MS Avaliação</span>
              </DataRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <DataRow>
                <span className="label">Família avaliativa</span>
                <span className="value">BOP-er</span>
              </DataRow>
              <DataRow>
                <span className="label">FAMILY LOCK</span>
                <span className="value strong">BOP-er</span>
              </DataRow>
            </Grid>
          </Grid>
        </ReportPanel>

        <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
          <Grid item xs={12} md={6}>
            <ReportPanel>
              <PanelTitle>Executive Decision</PanelTitle>
              <ExecutiveScore>
                <div>
                  <div className="score-caption">Job Fit MS</div>
                  <div className="score">84</div>
                </div>
                <div>
                  <div className="decision-label">ALTA ADERÊNCIA</div>
                  <div className="validated">
                    <CheckCircle /> MATCH VALIDADO
                  </div>
                </div>
              </ExecutiveScore>
            </ReportPanel>
          </Grid>
          <Grid item xs={12} md={6}>
            <ReportPanel>
              <PanelTitle>MS Match Track</PanelTitle>
              <MatchTrack>
                <div className="track-score" style={{ left: '84%' }}>
                  84
                </div>
                <div className="track-labels">
                  <span>Baixa</span>
                  <span>Parcial</span>
                  <span>Moderada</span>
                  <span>Alta</span>
                  <span>Muito Alta</span>
                </div>
                <div className="track-bar">
                  <span style={{ backgroundColor: reportColors.danger }} />
                  <span style={{ backgroundColor: '#fb923c' }} />
                  <span style={{ backgroundColor: reportColors.warning }} />
                  <span style={{ backgroundColor: '#4ade80' }} />
                  <span style={{ backgroundColor: reportColors.success }} />
                  <div className="track-marker" style={{ left: '84%' }} />
                </div>
                <div className="track-scale">
                  <span>0</span>
                  <span>20</span>
                  <span>40</span>
                  <span>60</span>
                  <span>80</span>
                  <span>100</span>
                </div>
              </MatchTrack>
            </ReportPanel>
          </Grid>
        </Grid>

        <ReportPanel>
          <PanelTitle>Resumo Executivo</PanelTitle>
          <SummaryText>
            Perfil com alta aderência ao Cargo DNA estimado, com destaque para
            execução, organização, comunicação e bom controle de risco. A função
            exige forte conformidade, atenção e segurança operacional; o
            candidato apresenta base favorável, com atenção dividida e alternada
            elevadas. Recomenda-se continuidade, monitorando controle emocional
            e aderência contínua a procedimentos críticos.
          </SummaryText>
        </ReportPanel>
      </ReportPage>

      {/* Página 2 — Structural */}
      <ReportPage>
        <ReportHeader />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ReportPanel>
              <PanelTitle>
                Radar das 12 Competências Universais — MCG Base
              </PanelTitle>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <RadarChart data={radarData} outerRadius="75%">
                    <PolarGrid stroke="rgba(148, 163, 184, 0.35)" />
                    <PolarAngleAxis
                      dataKey="axis"
                      tick={{ fill: reportColors.foreground, fontSize: 11 }}
                    />
                    <PolarRadiusAxis
                      tick={false}
                      axisLine={false}
                      domain={[0, 100]}
                    />
                    <Radar
                      name="Cargo DNA"
                      dataKey="dna"
                      stroke="#818cf8"
                      fill="#818cf8"
                      fillOpacity={0.4}
                    />
                    <Radar
                      name="Candidato"
                      dataKey="candidato"
                      stroke={reportColors.accent}
                      fill={reportColors.accent}
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <RadarLegend>
                <span>
                  <i style={{ backgroundColor: '#818cf8' }} /> Cargo DNA
                </span>
                <span>
                  <i style={{ backgroundColor: reportColors.accent }} />{' '}
                  Candidato
                </span>
              </RadarLegend>
            </ReportPanel>
          </Grid>
          <Grid item xs={12} md={6}>
            <ReportPanel>
              <PanelTitle>MS Structural Index</PanelTitle>
              {attention.map((item) => (
                <BarRow key={item.code}>
                  <div className="bar-heading">
                    <span>
                      {item.code} — {item.label}
                    </span>
                    <span className="bar-value">{item.value}</span>
                  </div>
                  <BarTrack>
                    <BarFill value={item.value} tone={item.tone} />
                  </BarTrack>
                  <div className="bar-tag">{item.tag}</div>
                </BarRow>
              ))}
            </ReportPanel>
          </Grid>
        </Grid>
      </ReportPage>

      {/* Página 3 — Comparison */}
      <ReportPage>
        <ReportHeader />
        <PanelTitle style={{ fontSize: 20, marginBottom: 20 }}>
          Comparação MCG v4.1 — Candidato x Cargo DNA
        </PanelTitle>
        <ReportPanel>
          <ComparisonTableWrapper>
            <table>
              <thead>
                <tr>
                  <th>Competência Universal</th>
                  <th>Cand.</th>
                  <th>DNA</th>
                  <th>Gap</th>
                  <th>Criticidade</th>
                  <th>Match</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.c}>
                    <td>{row.c}</td>
                    <td>{row.cand}</td>
                    <td style={{ color: reportColors.muted }}>{row.dna}</td>
                    <td
                      style={{
                        fontWeight: 700,
                        color:
                          row.gap > 0
                            ? reportColors.success
                            : row.gap < 0
                            ? reportColors.danger
                            : reportColors.muted,
                      }}
                    >
                      {row.gap > 0 ? `+${row.gap}` : row.gap}
                    </td>
                    <td>{row.crit}</td>
                    <td
                      style={{ color: reportColors.success, fontWeight: 600 }}
                    >
                      Adeq.
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ComparisonTableWrapper>
        </ReportPanel>
      </ReportPage>

      {/* Página 4 — Layers + PDI */}
      <ReportPage sx={{ marginBottom: 0 }}>
        <ReportHeader />
        <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
          <Grid item xs={12} md={6}>
            <ReportPanel>
              <PanelTitle>MCG Layers</PanelTitle>
              {mcgLayers.map((layer) => (
                <BarRow key={layer.label}>
                  <div className="bar-heading">
                    <span>{layer.label}</span>
                    <span className="bar-value">{layer.value}</span>
                  </div>
                  <BarTrack>
                    <BarFill value={layer.value} tone={layer.tone} />
                  </BarTrack>
                  <div className="bar-tag">{layer.tag}</div>
                </BarRow>
              ))}
            </ReportPanel>
          </Grid>
          <Grid item xs={12} md={6}>
            <ReportPanel>
              <PanelTitle>MCG External Layers</PanelTitle>
              <EmptyState>
                <div>
                  <VerifiedUser />
                  <div className="empty-title">Layer externo não avaliado.</div>
                  <div className="empty-description">
                    Nenhum instrumento externo adicional foi enviado para este
                    processamento.
                  </div>
                </div>
              </EmptyState>
            </ReportPanel>
          </Grid>
        </Grid>

        <ReportPanel>
          <PanelTitle>PDI | Programa de Desenvolvimento Individual</PanelTitle>
          <PdiGrid>
            {pdi.map((item) => (
              <PdiCard key={item.n}>
                <div className="priority">Prioridade {item.n}</div>
                <div className="focus">
                  <span className="focus-label">Foco:</span>
                  <span className="focus-value">{item.focus}</span>
                </div>

                <div className="section-label">Objetivo</div>
                <p>{item.obj}</p>

                <div className="section-label accent">
                  Treinamentos / Trilhas sugeridas
                </div>
                <ul>
                  {item.trilhas.map((trilha) => (
                    <li key={trilha}>{trilha}</li>
                  ))}
                </ul>

                <div className="section-label">Ganho esperado</div>
                <p>{item.ganho}</p>
              </PdiCard>
            ))}
          </PdiGrid>
        </ReportPanel>
      </ReportPage>
    </div>
  );
};
